import type { Localized } from '../types/notice'
import type { DashboardStats } from '../data/requestDashboard'
import { POTHOLE_ON_TIME } from '../data/requestDashboard'
import { fmtDec, fmtNum, pctChange, type AttentionTarget } from './requestDashboard'

/**
 * "Votre briefing territorial", prepared by ATNA from the dashboard figures.
 * Each card groups the requests tied to one situation and separates
 * facts, documented explanations and suggestions.
 */

export type BriefingBadge = 'rising' | 'unblock' | 'followup' | 'share'
export type DraftKind = 'followup' | 'citizen'

export interface BriefingDraft {
  kind: DraftKind
  to: Localized
  subject: Localized
  body: Localized
}

export interface BriefingExplanation {
  /** documented: cause in the file · partial: some of it · unknown: nothing recorded. */
  status: 'documented' | 'partial' | 'unknown'
  text: Localized
  source?: Localized
}

export interface BriefingEngagement {
  label: Localized
  owner: Localized
  confirmedOn: Localized
  dueDate: Localized
  progress: number
  progressLabel: Localized
}

export interface BriefingItem {
  id: string
  badge: BriefingBadge
  title: Localized
  /** Facts: trend, volume, location. */
  summary: Localized
  why: Localized
  explanation?: BriefingExplanation
  engagement?: BriefingEngagement
  nextStep: Localized
  drafts: BriefingDraft[]
  link: { label: Localized; target: AttentionTarget }
}

export interface SinceLastVisit {
  lastVisit: Localized
  newIssues: { count: number; detail: Localized }
  worsened: { count: number; detail: Localized }
  resolved: { count: number; detail: Localized }
}

export interface TerritorialBriefing {
  since: SinceLastVisit
  issues: BriefingItem[]
  result: BriefingItem
}

const L = (fr: string, en: string): Localized => ({ fr, en })

const UNKNOWN = (fr: string, en: string): BriefingExplanation => ({
  status: 'unknown',
  text: L(fr, en),
})

function lightingItem(stats: DashboardStats, where: Localized, sectors: Localized): BriefingItem {
  const l = stats.issues[0]
  return {
    id: 'lighting',
    badge: 'rising',
    title: L('Éclairage public : les signalements augmentent', 'Street lighting: reports are rising'),
    summary: L(
      `${fmtNum(l.count, 'fr')} demandes sur la période contre ${fmtNum(l.previous, 'fr')} la précédente (${pctChange(l.count, l.previous, 'fr')}). 1re catégorie de demandes ${where.fr} ; ${sectors.fr}.`,
      `${fmtNum(l.count, 'en')} requests over the period versus ${fmtNum(l.previous, 'en')} the previous one (${pctChange(l.count, l.previous, 'en')}). Top request category ${where.en}; ${sectors.en}.`,
    ),
    why: L(
      'Les délais restent respectés (92 % pour la réparation de lampadaires), mais la hausse touche plusieurs secteurs à la fois et aucune explication n’est consignée aux dossiers.',
      'Deadlines are still met (92% for streetlight repair), but the rise affects several sectors at once and no explanation is recorded in the files.',
    ),
    explanation: UNKNOWN(
      'Cause non documentée. Demandez une explication à la direction avant de conclure.',
      'Cause not documented. Ask the department for an explanation before drawing conclusions.',
    ),
    nextStep: L(
      'Demander aux Travaux publics d’expliquer la hausse et de préciser si une cause commune est identifiée.',
      'Ask Public Works to explain the rise and say whether a common cause has been identified.',
    ),
    drafts: [
      {
        kind: 'followup',
        to: L('Direction des Travaux publics', 'Public Works department'),
        subject: L(
          'Demande de suivi : hausse des signalements d’éclairage public',
          'Follow-up request: rise in street lighting reports',
        ),
        body: L(
          `Bonjour,\n\nLe tableau de bord des requêtes indique ${fmtNum(l.count, 'fr')} demandes liées à l’éclairage public ${where.fr} sur la période, contre ${fmtNum(l.previous, 'fr')} la période précédente (${pctChange(l.count, l.previous, 'fr')}).\n\nPourriez-vous m’indiquer :\n- si une cause commune a été identifiée (circuit, équipement, secteur) ;\n- les mesures prévues pour les prochaines semaines ?\n\nMerci,`,
          `Hello,\n\nThe request dashboard shows ${fmtNum(l.count, 'en')} street lighting requests ${where.en} over the period, versus ${fmtNum(l.previous, 'en')} the previous period (${pctChange(l.count, l.previous, 'en')}).\n\nCould you tell me:\n- whether a common cause has been identified (circuit, equipment, sector);\n- what measures are planned for the coming weeks?\n\nThank you,`,
        ),
      },
    ],
    link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
  }
}

function resultItem(stats: DashboardStats, where: Localized, extra: Localized): BriefingItem {
  const s = stats
  return {
    id: 'result',
    badge: 'share',
    title: L(
      `${s.onTime} % des demandes résolues dans les délais`,
      `${s.onTime}% of requests resolved on time`,
    ),
    summary: L(
      `${s.onTimePrev} % la période précédente. Délai médian de ${fmtDec(s.medianDays, 'fr')} j (contre ${fmtDec(s.medianDaysPrev, 'fr')} j). Satisfaction de ${fmtDec(s.satisfaction, 'fr')} / 5 sur ${fmtNum(s.surveyResponses, 'fr')} réponses au sondage. ${extra.fr}`,
      `${s.onTimePrev}% the previous period. Median time of ${fmtDec(s.medianDays, 'en')} d (versus ${fmtDec(s.medianDaysPrev, 'en')} d). Satisfaction of ${fmtDec(s.satisfaction, 'en')} / 5 from ${fmtNum(s.surveyResponses, 'en')} survey responses. ${extra.en}`,
    ),
    why: L(
      'Les citoyens voient surtout les problèmes signalés. Montrer que les demandes sont traitées rapidement renforce la confiance et encourage à utiliser l’application.',
      'Citizens mostly see the problems they report. Showing that requests are handled quickly builds trust and encourages use of the app.',
    ),
    nextStep: L(
      `Partager ces résultats ${where.fr} et remercier les équipes.`,
      `Share these results ${where.en} and thank the teams.`,
    ),
    drafts: [
      {
        kind: 'citizen',
        to: L('Équipe Communications, pour publication', 'Communications team, for publication'),
        subject: L('Vos demandes traitées plus rapidement', 'Your requests handled faster'),
        body: L(
          `Merci pour vos signalements !\n\nCe mois-ci, ${s.onTime} % des demandes citoyennes ont été résolues dans les délais prévus, et la moitié l’ont été en moins de ${fmtDec(s.medianDays, 'fr')} jours.\n\nVous nous donnez une note de ${fmtDec(s.satisfaction, 'fr')} / 5 après traitement. Merci aux équipes de la Ville pour leur travail.\n\nContinuez à nous signaler tout problème depuis l’application bciti+.`,
          `Thank you for your reports!\n\nThis month, ${s.onTime}% of citizen requests were resolved within the expected time, and half of them in under ${fmtDec(s.medianDays, 'en')} days.\n\nYou rate our handling ${fmtDec(s.satisfaction, 'en')} / 5. Thanks to the City’s teams for their work.\n\nKeep reporting any issue from the bciti+ app.`,
        ),
      },
    ],
    link: { label: L('Voir le détail par service', 'View details by service'), target: 'sla-table' },
  }
}

function cityBriefing(stats: DashboardStats): TerritorialBriefing {
  const potholes = stats.issues[1]
  return {
    since: {
      lastVisit: L('23 sept.', 'Sept. 23'),
      newIssues: { count: 1, detail: L('Hausse de l’éclairage public', 'Street lighting rise') },
      worsened: {
        count: 1,
        detail: L('Nids-de-poule passés sous le seuil de 80 %', 'Potholes fell below the 80% threshold'),
      },
      resolved: {
        count: stats.latePrev - stats.late,
        detail: L(
          `Retards résolus (${stats.latePrev} → ${stats.late})`,
          `Late requests resolved (${stats.latePrev} → ${stats.late})`,
        ),
      },
    },
    issues: [
      {
        id: 'sector-b',
        badge: 'unblock',
        title: L(
          'Secteur B : 3 demandes en retard ou proches de l’échéance',
          'Sector B: 3 requests late or close to deadline',
        ),
        summary: L(
          '1 nid-de-poule en retard (11 j pour un SLA de 7 j) et 2 réparations de bacs brisés à 12 j sur 14, toutes dans le secteur B (district 3). Ce sont les 3 seules demandes à risque de la ville.',
          '1 late pothole (11 d against a 7-day SLA) and 2 broken bin repairs at 12 of 14 days, all in sector B (district 3). They are the city’s only 3 requests at risk.',
        ),
        why: L(
          'Tout le retard de la ville se concentre sur un seul secteur. Sans intervention d’ici 2 jours, les 2 bacs passeront aussi en retard.',
          'All of the city’s delay sits in a single sector. Without action within 2 days, the 2 bins will be late too.',
        ),
        explanation: {
          status: 'partial',
          text: L(
            'Nid-de-poule : pluies abondantes, pose d’enrobé impossible sur chaussée détrempée. Bacs : aucune explication au dossier.',
            'Pothole: heavy rain, asphalt can’t be laid on waterlogged pavement. Bins: no explanation in the file.',
          ),
          source: L('Note au dossier · Travaux publics · 27 sept.', 'File note · Public Works · Sept. 27'),
        },
        engagement: {
          label: L('Réparation du nid-de-poule', 'Pothole repair'),
          owner: L('Travaux publics', 'Public Works'),
          confirmedOn: L('confirmé le 28 sept.', 'confirmed Sept. 28'),
          dueDate: L('2 oct. 2026', 'Oct. 2, 2026'),
          progress: 66,
          progressLabel: L('Enrobé commandé · équipe planifiée', 'Asphalt ordered · crew scheduled'),
        },
        nextStep: L(
          'Informer les résidents du secteur B de la date de réparation et demander une date d’intervention pour les 2 bacs.',
          'Tell sector B residents the repair date and ask for an intervention date for the 2 bins.',
        ),
        drafts: [
          {
            kind: 'citizen',
            to: L('Équipe Communications · résidents du secteur B', 'Communications team · sector B residents'),
            subject: L('Réparation de la chaussée dans votre secteur', 'Road repair in your sector'),
            body: L(
              'Bonjour,\n\nLes pluies abondantes des derniers jours ont retardé la réparation d’un nid-de-poule signalé dans votre secteur : l’enrobé ne peut pas être posé sur une chaussée détrempée.\n\nLes Travaux publics ont planifié la réparation le 2 octobre 2026. Merci de votre patience et de vos signalements.',
              'Hello,\n\nHeavy rain over the last few days delayed the repair of a pothole reported in your sector: asphalt can’t be laid on waterlogged pavement.\n\nPublic Works has scheduled the repair for October 2, 2026. Thank you for your patience and your reports.',
            ),
          },
          {
            kind: 'followup',
            to: L('Direction des Travaux publics', 'Public Works department'),
            subject: L(
              'Demande de suivi : 2 réparations de bacs, secteur B',
              'Follow-up request: 2 bin repairs, sector B',
            ),
            body: L(
              'Bonjour,\n\nDeux demandes de réparation de bacs brisés du secteur B sont ouvertes depuis 12 jours pour un SLA de 14 jours. Aucune explication n’est consignée aux dossiers.\n\nPourriez-vous me confirmer la date d’intervention prévue et, s’il y a lieu, la raison du délai ?\n\nMerci,',
              'Hello,\n\nTwo broken bin repair requests in sector B have been open for 12 days against a 14-day SLA. No explanation is recorded in the files.\n\nCould you confirm the planned intervention date and, if relevant, the reason for the delay?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir sur la carte', 'Show on map'), target: 'map-late' },
      },
      lightingItem(
        stats,
        L('de la ville', 'in the city'),
        L('les secteurs L, C et M en regroupent 41 %', 'sectors L, C and M account for 41%'),
      ),
      {
        id: 'potholes',
        badge: 'followup',
        title: L('Nids-de-poule : le délai de réparation se dégrade', 'Potholes: repair times are slipping'),
        summary: L(
          `${POTHOLE_ON_TIME.current} % des demandes résolues dans les délais, contre ${POTHOLE_ON_TIME.previous} % la période précédente, sous le seuil de conformité de ${POTHOLE_ON_TIME.threshold} %. ${fmtNum(potholes.count, 'fr')} demandes reçues (${pctChange(potholes.count, potholes.previous, 'fr')}).`,
          `${POTHOLE_ON_TIME.current}% of requests resolved on time, versus ${POTHOLE_ON_TIME.previous}% the previous period, below the ${POTHOLE_ON_TIME.threshold}% compliance threshold. ${fmtNum(potholes.count, 'en')} requests received (${pctChange(potholes.count, potholes.previous, 'en')}).`,
        ),
        why: L(
          'C’est le seul service sous le seuil. Le volume a peu varié : la baisse ne vient donc pas d’un afflux de demandes.',
          'It’s the only service below the threshold. Volume barely changed, so the drop doesn’t come from a surge in requests.',
        ),
        explanation: UNKNOWN(
          'Seul le retard du secteur B est expliqué (pluies). Rien au dossier pour la baisse générale.',
          'Only the sector B delay is explained (rain). Nothing in the files for the overall drop.',
        ),
        nextStep: L(
          'Demander aux Travaux publics un bilan des dossiers hors délai et les mesures prévues avant le gel.',
          'Ask Public Works for a review of late files and the measures planned before the freeze.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics', 'Public Works department'),
            subject: L(
              'Demande de suivi : respect des délais pour les nids-de-poule',
              'Follow-up request: on-time performance for potholes',
            ),
            body: L(
              `Bonjour,\n\nLe respect des délais pour les nids-de-poule est passé de ${POTHOLE_ON_TIME.previous} % à ${POTHOLE_ON_TIME.current} %, sous le seuil de ${POTHOLE_ON_TIME.threshold} %, alors que le volume de demandes a peu varié.\n\nPourriez-vous me transmettre :\n- les raisons de cette baisse ;\n- les mesures prévues avant la période de gel ?\n\nMerci,`,
              `Hello,\n\nOn-time performance for potholes went from ${POTHOLE_ON_TIME.previous}% to ${POTHOLE_ON_TIME.current}%, below the ${POTHOLE_ON_TIME.threshold}% threshold, while request volume barely changed.\n\nCould you send me:\n- the reasons for this drop;\n- the measures planned before the freeze?\n\nThank you,`,
            ),
          },
        ],
        link: { label: L('Voir le détail par service', 'View details by service'), target: 'sla-table' },
      },
    ],
    result: resultItem(
      stats,
      L('à l’échelle de la ville', 'city-wide'),
      L(
        `Demandes en retard passées de ${stats.latePrev} à ${stats.late} en 30 jours.`,
        `Late requests down from ${stats.latePrev} to ${stats.late} in 30 days.`,
      ),
    ),
  }
}

function district1Briefing(stats: DashboardStats): TerritorialBriefing {
  const potholes = stats.issues[1]
  return {
    since: {
      lastVisit: L('25 sept.', 'Sept. 25'),
      newIssues: { count: 1, detail: L('2 lampadaires en attente d’Hydro-Québec', '2 streetlights waiting on Hydro-Québec') },
      worsened: { count: 0, detail: L('Aucune situation aggravée', 'No situation worsened') },
      resolved: { count: 1, detail: L('Dernier retard du district résolu', 'District’s last late request resolved') },
    },
    issues: [
      lightingItem(
        stats,
        L('du district', 'in the district'),
        L('surtout dans les secteurs C et L', 'mostly in sectors C and L'),
      ),
      {
        id: 'hydro',
        badge: 'unblock',
        title: L(
          'Secteur E : 2 lampadaires en attente d’Hydro-Québec',
          'Sector E: 2 streetlights waiting on Hydro-Québec',
        ),
        summary: L(
          '2 demandes de réparation ouvertes depuis 6 jours (SLA 14 j), rue de Lausanne. Encore dans les délais, mais bloquées par une intervention externe.',
          '2 repair requests open for 6 days (14-day SLA), rue de Lausanne. Still on time, but blocked by an outside intervention.',
        ),
        why: L(
          'La date dépend d’un tiers : sans date confirmée, la Ville ne peut pas garantir le délai aux résidents.',
          'The date depends on a third party: without a confirmed date, the City can’t guarantee the deadline to residents.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Défaut sur le réseau d’alimentation ; le raccordement relève d’Hydro-Québec.',
            'Fault on the power network; the connection is Hydro-Québec’s responsibility.',
          ),
          source: L('Note au dossier · Éclairage public · 26 sept.', 'File note · Street lighting · Sept. 26'),
        },
        nextStep: L(
          'Demander à la direction d’obtenir une date d’intervention auprès d’Hydro-Québec.',
          'Ask the department to get an intervention date from Hydro-Québec.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics · Éclairage', 'Public Works department · Lighting'),
            subject: L(
              'Demande de suivi : lampadaires en attente d’Hydro-Québec, secteur E',
              'Follow-up request: streetlights waiting on Hydro-Québec, sector E',
            ),
            body: L(
              'Bonjour,\n\nDeux demandes de réparation de lampadaires de la rue de Lausanne (secteur E) sont en attente d’une intervention d’Hydro-Québec depuis le 26 septembre.\n\nPourriez-vous relancer Hydro-Québec et me confirmer la date d’intervention prévue, afin que je puisse informer les résidents ?\n\nMerci,',
              'Hello,\n\nTwo streetlight repair requests on rue de Lausanne (sector E) have been waiting for a Hydro-Québec intervention since September 26.\n\nCould you follow up with Hydro-Québec and confirm the planned date, so I can inform residents?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'potholes-l',
        badge: 'followup',
        title: L('Secteur L : campagne de colmatage en cours', 'Sector L: pothole patching campaign under way'),
        summary: L(
          `${fmtNum(potholes.count, 'fr')} demandes de nids-de-poule dans le district (${pctChange(potholes.count, potholes.previous, 'fr')}), dont 21 dans le secteur L. À l’échelle de la ville, ce service est à ${POTHOLE_ON_TIME.current} % dans les délais, sous le seuil de ${POTHOLE_ON_TIME.threshold} %.`,
          `${fmtNum(potholes.count, 'en')} pothole requests in the district (${pctChange(potholes.count, potholes.previous, 'en')}), including 21 in sector L. City-wide, this service is at ${POTHOLE_ON_TIME.current}% on time, below the ${POTHOLE_ON_TIME.threshold}% threshold.`,
        ),
        why: L(
          'Le secteur L regroupe plus d’un tiers des demandes du district, et les Travaux publics se sont engagés sur une date.',
          'Sector L holds more than a third of the district’s requests, and Public Works has committed to a date.',
        ),
        engagement: {
          label: L('Colmatage du secteur L', 'Sector L patching'),
          owner: L('Travaux publics', 'Public Works'),
          confirmedOn: L('confirmé le 25 sept.', 'confirmed Sept. 25'),
          dueDate: L('9 oct. 2026', 'Oct. 9, 2026'),
          progress: 62,
          progressLabel: L('13 réparations sur 21', '13 of 21 repairs'),
        },
        nextStep: L(
          'Suivre l’avancement et informer les résidents du secteur L à la fin de la campagne.',
          'Track progress and inform sector L residents when the campaign ends.',
        ),
        drafts: [
          {
            kind: 'citizen',
            to: L('Équipe Communications · résidents du secteur L', 'Communications team · sector L residents'),
            subject: L('Réparation des nids-de-poule dans votre secteur', 'Pothole repairs in your sector'),
            body: L(
              'Bonjour,\n\nLes Travaux publics mènent une campagne de colmatage des nids-de-poule dans votre secteur : 13 des 21 réparations signalées sont déjà faites, et les autres sont prévues d’ici le 9 octobre 2026.\n\nMerci pour vos signalements.\n\nChristian Gaudette, conseiller municipal — District 1',
              'Hello,\n\nPublic Works is running a pothole patching campaign in your sector: 13 of the 21 reported repairs are already done, and the rest are planned by October 9, 2026.\n\nThank you for your reports.\n\nChristian Gaudette, city councillor — District 1',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
    ],
    result: resultItem(
      stats,
      L('aux résidents du district', 'with district residents'),
      L('Aucune demande en retard à ce jour.', 'No late request as of today.'),
    ),
  }
}

function district4Briefing(stats: DashboardStats): TerritorialBriefing {
  const potholes = stats.issues[1]
  return {
    since: {
      lastVisit: L('24 sept.', 'Sept. 24'),
      newIssues: { count: 1, detail: L('Panneau en attente du ministère', 'Sign waiting on the ministry') },
      worsened: { count: 1, detail: L('Satisfaction en légère baisse', 'Satisfaction slightly down') },
      resolved: { count: 0, detail: L('Aucun retard à résoudre', 'No late request to resolve') },
    },
    issues: [
      lightingItem(
        stats,
        L('du district', 'in the district'),
        L('toutes dans le secteur A', 'all in sector A'),
      ),
      {
        id: 'mtq',
        badge: 'unblock',
        title: L(
          'Boulevard Taschereau : panneau en attente du ministère',
          'Boulevard Taschereau: sign waiting on the ministry',
        ),
        summary: L(
          '1 demande de remplacement d’un panneau de limite de vitesse endommagé, ouverte depuis 5 jours, encore dans les délais. Transmise au ministère des Transports le 27 sept.',
          '1 request to replace a damaged speed limit sign, open for 5 days, still on time. Sent to the Ministère des Transports on Sept. 27.',
        ),
        why: L(
          'Un panneau de vitesse manquant touche la sécurité, et la Ville ne contrôle pas la date d’intervention.',
          'A missing speed sign affects safety, and the City doesn’t control the intervention date.',
        ),
        explanation: {
          status: 'documented',
          text: L(
            'Le boulevard Taschereau (route 134) relève du ministère des Transports.',
            'Boulevard Taschereau (Route 134) is under the Ministère des Transports.',
          ),
          source: L('Note au dossier · Signalisation · 27 sept.', 'File note · Signage · Sept. 27'),
        },
        nextStep: L(
          'Demander au service de relancer le ministère et de confirmer une date.',
          'Ask the department to follow up with the ministry and confirm a date.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Direction des Travaux publics · Signalisation', 'Public Works department · Signage'),
            subject: L(
              'Demande de suivi : panneau de vitesse, boulevard Taschereau',
              'Follow-up request: speed sign, boulevard Taschereau',
            ),
            body: L(
              'Bonjour,\n\nLa demande de remplacement d’un panneau de limite de vitesse sur le boulevard Taschereau a été transmise au ministère des Transports le 27 septembre.\n\nPourriez-vous relancer le ministère et me confirmer la date d’intervention prévue ?\n\nMerci,',
              'Hello,\n\nThe request to replace a speed limit sign on boulevard Taschereau was sent to the Ministère des Transports on September 27.\n\nCould you follow up with the ministry and confirm the planned date?\n\nThank you,',
            ),
          },
        ],
        link: { label: L('Voir les requêtes', 'View requests'), target: 'map-open' },
      },
      {
        id: 'satisfaction',
        badge: 'followup',
        title: L('Satisfaction : légère baisse à confirmer', 'Satisfaction: slight drop to confirm'),
        summary: L(
          `${fmtDec(stats.satisfaction, 'fr')} / 5 après traitement, contre ${fmtDec(stats.satisfactionPrev, 'fr')} la période précédente, sur ${fmtNum(stats.surveyResponses, 'fr')} réponses au sondage.`,
          `${fmtDec(stats.satisfaction, 'en')} / 5 after handling, versus ${fmtDec(stats.satisfactionPrev, 'en')} the previous period, from ${fmtNum(stats.surveyResponses, 'en')} survey responses.`,
        ),
        why: L(
          'L’écart est faible et repose sur peu de réponses : il est trop tôt pour conclure, mais les commentaires peuvent révéler un irritant précis.',
          'The gap is small and based on few responses: too early to conclude, but the comments may reveal a specific irritant.',
        ),
        explanation: UNKNOWN(
          'Cause non documentée. Les commentaires du sondage n’ont pas encore été analysés.',
          'Cause not documented. Survey comments haven’t been analysed yet.',
        ),
        nextStep: L(
          'Demander à Services Brossard un résumé des commentaires reçus dans le district.',
          'Ask Services Brossard for a summary of comments received in the district.',
        ),
        drafts: [
          {
            kind: 'followup',
            to: L('Services Brossard', 'Services Brossard'),
            subject: L(
              'Demande de suivi : commentaires du sondage, district 4',
              'Follow-up request: survey comments, district 4',
            ),
            body: L(
              `Bonjour,\n\nLa satisfaction après traitement dans le district 4 est de ${fmtDec(stats.satisfaction, 'fr')} / 5 sur ${fmtNum(stats.surveyResponses, 'fr')} réponses, contre ${fmtDec(stats.satisfactionPrev, 'fr')} la période précédente.\n\nPourriez-vous me transmettre un résumé des commentaires reçus, afin de voir si un irritant particulier ressort ?\n\nMerci,`,
              `Hello,\n\nSatisfaction after handling in district 4 is ${fmtDec(stats.satisfaction, 'en')} / 5 from ${fmtNum(stats.surveyResponses, 'en')} responses, versus ${fmtDec(stats.satisfactionPrev, 'en')} the previous period.\n\nCould you send me a summary of the comments received, to see whether a specific irritant stands out?\n\nThank you,`,
            ),
          },
        ],
        link: { label: L('Voir les résultats par service', 'View results by service'), target: 'sla-table' },
      },
    ],
    result: resultItem(
      stats,
      L('aux résidents du district', 'with district residents'),
      L(
        `Aucune demande en retard, et ${pctChange(potholes.count, potholes.previous, 'fr')} de nids-de-poule signalés.`,
        `No late request, and ${pctChange(potholes.count, potholes.previous, 'en')} pothole reports.`,
      ),
    ),
  }
}

/** Briefing for the selected territory: a councillor's district or the whole city. */
export function buildBriefing(stats: DashboardStats, districtId: string | null): TerritorialBriefing {
  if (districtId === 'd1') return district1Briefing(stats)
  if (districtId === 'd4') return district4Briefing(stats)
  return cityBriefing(stats)
}
